import { useEffect, useRef } from 'react';
import { t } from '@app/i18n';
import { dataHour } from '@app/format';
import { Badge, Button, ErrorBanner } from '@zarya/ui';
import { useAdmissionVersions } from './SiteAdmission.api';
import { changeLabel, changeValue, type AdmissionVersion } from './SiteAdmissionModel';
import dialog from '../Sites.module.css';
import s from './SiteAdmission.module.css';

export interface AdmissionHistoryDialogProps {
  siteId: string;
  siteName: string;
  /** Restaurar põe a configuração da versão no formulário; quem salva é o «Revisar e aplicar» (protótipo §5). */
  onRestore: (version: AdmissionVersion) => void;
  onClose: () => void;
}

const DELIVERY_TONE: Record<AdmissionVersion['delivery'], 'success' | 'warning' | 'danger'> = {
  delivered: 'success',
  pending: 'warning',
  failed: 'danger',
};

/**
 * «Ver histórico» (protótipo §5): versão, quem, quando, motivo e o diff de cada uma. Restaurar não grava nada aqui:
 * cria uma versão nova com o conteúdo antigo, pelo mesmo diálogo de toda aplicação, com motivo.
 */
export function AdmissionHistoryDialog({ siteId, siteName, onRestore, onClose }: AdmissionHistoryDialogProps) {
  const versions = useAdmissionVersions(siteId, true);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const items = versions.data ?? [];

  return (
    <div className={dialog.overlay}>
      <div className={dialog.mbox} role="dialog" aria-modal="true" aria-labelledby="admission-history-title">
        <div className={dialog.mh}>
          <h3 id="admission-history-title">{t('sites.admission.history.title', { site: siteName })}</h3>
        </div>
        <div className={dialog.mb}>
          {versions.isPending ? <p>{t('sites.admission.history.loading')}</p> : null}
          {versions.error ? <ErrorBanner error={versions.error} title={t('sites.admission.history.error')} hideLink /> : null}
          {versions.data && items.length === 0 ? <p>{t('sites.admission.history.empty')}</p> : null}
          <ol className={s.history} aria-label={t('sites.admission.history.listLabel')}>
            {items.map((version, index) => (
              <li key={version.version} className={s.historyItem} data-testid="admission-version">
                <div className={s.historyHead}>
                  <strong>{t('sites.admission.history.version', { version: version.version })}</strong>
                  <span>
                    {t('sites.admission.history.by', {
                      who: version.created_by,
                      when: dataHour(version.created_at) ?? '—',
                    })}
                  </span>
                  <Badge tone={DELIVERY_TONE[version.delivery]}>
                    {t(`sites.admission.delivery.${version.delivery}`)}
                  </Badge>
                  {version.trigger !== 'manual' ? (
                    <Badge tone="neutral">{t(`sites.admission.trigger.${version.trigger}`)}</Badge>
                  ) : null}
                  {index > 0 ? (
                    <span className={s.historyActions}>
                      <Button variant="soft" icon="refresh" onClick={() => onRestore(version)}>
                        {t('sites.admission.history.restore')}
                      </Button>
                    </span>
                  ) : null}
                </div>
                <p className={s.note}>{t('sites.admission.history.reason', { reason: version.reason })}</p>
                {version.changes.length > 0 && version.version > 1 ? (
                  <table className={s.diff}>
                    <tbody>
                      {version.changes.map((change) => (
                        <tr key={change.field}>
                          <td>{changeLabel(change.field)}</td>
                          <td>{changeValue(change.field, change.before)}</td>
                          <td>{changeValue(change.field, change.after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
        <div className={dialog.mf}>
          <Button ref={closeRef} variant="out" onClick={onClose}>
            {t('sites.admission.history.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
